import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Typography,
} from '@mui/material';
import { Trash } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { isGlobalNode } from '../../constants/globalNode';
import { useNotificationStore } from '../../store/storeNotifications';

const StyledIndicator = styled(Paper)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

interface MultiSelectIndicatorProps {
  selectedCount: number;
  selectedNodes: any[];
  onDeleteNodes: (nodes: any[]) => void;
  onCancelSelection: () => void;
}

const MultiSelectIndicator = ({
  selectedCount,
  selectedNodes,
  onDeleteNodes,
  onCancelSelection,
}: MultiSelectIndicatorProps) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const setNotification = useNotificationStore(state => state.setNotification);

  const deletableNodes = useMemo(
    () => selectedNodes.filter(node => !isGlobalNode(node.id)),
    [selectedNodes],
  );
  const blockedCount = selectedNodes.length - deletableNodes.length;
  const deletableCount = deletableNodes.length;

  const openDeleteDialog = useCallback(() => {
    if (selectedCount > 1) {
      setDeleteDialogOpen(true);
    }
  }, [selectedCount]);

  useEffect(() => {
    if (selectedCount <= 1) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const isInput =
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement)?.contentEditable === 'true';

      if (isInput || deleteDialogOpen) return;

      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        openDeleteDialog();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedCount, deleteDialogOpen, openDeleteDialog]);

  const handleDeleteClick = useCallback(() => {
    if (selectedCount > 0) {
      setDeleteDialogOpen(true);
    }
  }, [selectedCount]);

  const handleDeleteConfirm = useCallback(() => {
    if (!onDeleteNodes || selectedNodes.length === 0) return;
    if (deletableNodes.length > 0) {
      onDeleteNodes(deletableNodes);
    }
    if (blockedCount > 0) {
      setNotification({
        type: 'warning',
        message: 'Global node cannot be deleted.',
      });
    }
    setDeleteDialogOpen(false);
  }, [
    onDeleteNodes,
    selectedNodes,
    deletableNodes,
    blockedCount,
    setNotification,
  ]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  if (selectedCount <= 1) {
    return null;
  }

  return (
    <>
      <StyledIndicator>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {selectedCount} nodes selected
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={onCancelSelection}
          sx={{
            minWidth: 'auto',
            px: 1.5,
            py: 0.5,
            fontSize: '0.75rem',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          size="small"
          startIcon={<Trash size={16} />}
          onClick={handleDeleteClick}
          sx={{
            minWidth: 'auto',
            px: 1.5,
            py: 0.5,
            fontSize: '0.75rem',
          }}
        >
          Delete
        </Button>
      </StyledIndicator>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete {deletableCount} node{deletableCount === 1 ? '' : 's'}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete {deletableCount} selected node
            {deletableCount === 1 ? '' : 's'}? This action cannot be undone.
            {blockedCount > 0 && (
              <>
                <br />
                <strong>Note:</strong> the global node cannot be deleted and
                will be skipped.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            autoFocus
            disabled={deletableCount === 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MultiSelectIndicator;

